import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  Paper,
  BottomNavigation,
  BottomNavigationAction,
  Tooltip,
} from '@mui/material'
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  ShoppingCart as ShoppingCartIcon,
  PointOfSale as PointOfSaleIcon,
  Description as DescriptionIcon,
  UploadFile as UploadFileIcon,
  CloudDownload as CloudDownloadIcon,
  Assessment as AssessmentIcon,
  FileOpen as FileOpenIcon,
  Percent as PercentIcon,
  MoreHoriz as MoreHorizIcon,
  AccountCircle,
  ExitToApp,
} from '@mui/icons-material'
import { useAuth } from '../../context/AuthContext'
import { drawerWidth, bottomNavHeight } from '../../theme/tokens'

/**
 * Navegación agrupada por dominio funcional.
 * Agrupar reduce la carga cognitiva frente a una lista plana de 11 enlaces.
 */
const navSections = [
  {
    label: 'General',
    items: [{ text: 'Dashboard', icon: <DashboardIcon />, path: '/' }],
  },
  {
    label: 'Transacciones',
    items: [
      { text: 'Compras', icon: <ShoppingCartIcon />, path: '/compras' },
      { text: 'Importar XML', icon: <UploadFileIcon />, path: '/importar-xml' },
      { text: 'Ventas', icon: <PointOfSaleIcon />, path: '/ventas' },
      { text: 'Importar Ventas SRI', icon: <CloudDownloadIcon />, path: '/importar-ventas-sri' },
    ],
  },
  {
    label: 'Anexo ATS',
    items: [
      { text: 'Generar ATS', icon: <DescriptionIcon />, path: '/generar-ats' },
      { text: 'Importar ATS', icon: <FileOpenIcon />, path: '/importar-ats' },
      { text: 'Reportes', icon: <AssessmentIcon />, path: '/reportes' },
    ],
  },
  {
    label: 'Administración',
    items: [
      { text: 'Empresas', icon: <BusinessIcon />, path: '/empresas' },
      { text: 'Usuarios', icon: <PeopleIcon />, path: '/usuarios' },
      { text: 'Códigos Retención', icon: <PercentIcon />, path: '/codigos-retencion' },
    ],
  },
]

/** Accesos directos de la barra inferior en móvil: las 4 rutas más usadas. */
const bottomNavItems = [
  { text: 'Inicio', icon: <DashboardIcon />, path: '/' },
  { text: 'Compras', icon: <ShoppingCartIcon />, path: '/compras' },
  { text: 'Ventas', icon: <PointOfSaleIcon />, path: '/ventas' },
  { text: 'ATS', icon: <DescriptionIcon />, path: '/generar-ats' },
]

function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev)
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget)
  const handleMenuClose = () => setAnchorEl(null)

  const handleLogout = () => {
    handleMenuClose()
    logout()
  }

  const isActive = (path) => (path === '/' ? pathname === '/' : pathname.startsWith(path))

  const go = (path) => {
    navigate(path)
    setMobileOpen(false)
  }

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ gap: 1.5, px: 2.5 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.5,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            display: 'grid',
            placeItems: 'center',
            fontWeight: 700,
            fontSize: '0.875rem',
            flexShrink: 0,
          }}
        >
          AT
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Sistema ATS
          </Typography>
          <Typography variant="caption" noWrap color="text.secondary">
            SRI Ecuador
          </Typography>
        </Box>
      </Toolbar>
      <Divider />

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1 }}>
        {navSections.map((section) => (
          <List
            key={section.label}
            dense
            disablePadding
            sx={{ mb: 1 }}
            subheader={
              <ListSubheader
                disableSticky
                sx={{
                  bgcolor: 'transparent',
                  color: 'text.secondary',
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  lineHeight: 2.5,
                  px: 1.5,
                }}
              >
                {section.label}
              </ListSubheader>
            }
          >
            {section.items.map((item) => (
              <ListItem key={item.text} disablePadding sx={{ mb: 0.25 }}>
                <ListItemButton
                  selected={isActive(item.path)}
                  onClick={() => go(item.path)}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{ fontSize: '0.9375rem' }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        ))}
      </Box>

      {/* Identidad del usuario al pie: siempre visible sin abrir menús */}
      <Divider />
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <Avatar sx={{ bgcolor: 'secondary.main', width: 36, height: 36, fontSize: '0.875rem' }}>
          {user?.nombre?.charAt(0)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" noWrap fontWeight={600}>
            {user?.nombre} {user?.apellido}
          </Typography>
          <Typography variant="caption" noWrap color="text.secondary" component="div">
            {user?.rol?.replace(/_/g, ' ')}
          </Typography>
        </Box>
      </Box>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            aria-label="Abrir menú de navegación"
            sx={{ display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            noWrap
            component="h1"
            sx={{ flexGrow: 1, minWidth: 0, fontSize: { xs: '1rem', md: '1.0625rem' } }}
          >
            <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
              Anexo Transaccional Simplificado
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
              Sistema ATS
            </Box>
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{ display: { xs: 'none', md: 'block' }, maxWidth: 220 }}
          >
            {user?.nombre} {user?.apellido}
          </Typography>

          <Tooltip title="Cuenta">
            <IconButton
              onClick={handleMenuOpen}
              aria-label="Abrir menú de cuenta"
              aria-haspopup="true"
              sx={{ p: 0.5 }}
            >
              <Avatar sx={{ bgcolor: 'secondary.main', width: 34, height: 34, fontSize: '0.875rem' }}>
                {user?.nombre?.charAt(0)}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { minWidth: 220, mt: 1 } } }}
          >
            <MenuItem disabled sx={{ opacity: '1 !important' }}>
              <AccountCircle sx={{ mr: 1.5, color: 'text.secondary' }} />
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {user?.nombre} {user?.apellido}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.rol?.replace(/_/g, ' ')}
                </Typography>
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ExitToApp sx={{ mr: 1.5 }} />
              Cerrar Sesión
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              pt: 'env(safe-area-inset-top, 0px)',
              pb: 'env(safe-area-inset-bottom, 0px)',
            },
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              pt: 'env(safe-area-inset-top, 0px)',
              pl: 'env(safe-area-inset-left, 0px)',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
          minWidth: 0,
          minHeight: '100dvh',
          bgcolor: 'background.default',
          overflowX: 'hidden',
          px: { xs: 2, sm: 3, md: 4 },
          pt: { xs: 2, sm: 3 },
          // Deja hueco para la barra inferior móvil y el home indicator de iOS
          pb: {
            xs: `calc(${bottomNavHeight}px + env(safe-area-inset-bottom, 0px) + 24px)`,
            sm: 4,
          },
          pr: { xs: 'calc(16px + env(safe-area-inset-right, 0px))', sm: 3, md: 4 },
        }}
      >
        {/* Espaciador equivalente a la AppBar fija, incluida su safe area superior */}
        <Box sx={(t) => ({ ...t.mixins.toolbar, mt: 'env(safe-area-inset-top, 0px)', mb: 1 })} />
        {children}
      </Box>

      {/* Barra de navegación inferior: alcanzable con el pulgar, solo en móvil */}
      <Paper
        elevation={0}
        sx={{
          display: { xs: 'block', sm: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: (t) => t.zIndex.appBar,
          borderRadius: 0,
          borderTop: 1,
          borderColor: 'divider',
          pb: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <BottomNavigation
          showLabels
          value={bottomNavItems.findIndex((i) => isActive(i.path))}
          sx={{ height: bottomNavHeight, bgcolor: 'transparent' }}
        >
          {bottomNavItems.map((item) => (
            <BottomNavigationAction
              key={item.path}
              label={item.text}
              icon={item.icon}
              onClick={() => navigate(item.path)}
            />
          ))}
          <BottomNavigationAction
            label="Más"
            icon={<MoreHorizIcon />}
            onClick={handleDrawerToggle}
            aria-label="Abrir menú de navegación completo"
          />
        </BottomNavigation>
      </Paper>
    </Box>
  )
}

export default Layout
